import type { FindFn } from "@shared/models/analytics/base"
import { createBlankEvent } from "@shared/models/analytics/base"
import type { LessonEditAttachment } from "@shared/models/analytics/lesson"
import { getPossessiveForPlanbook } from "@shared/models/analytics/planbook"
import type { CardAttachment } from "@shared/models/card-stack"
import Patch from "@shared/models/patch-updater"
import { assign, cloneDeep, find, get } from "lodash-es"
import type { Narrative } from "@shared/actions/create-narrative"
import defaultLessonActivity from "./default-lesson-activity"
import defaultLessonContext from "./default-lesson-context"
import defaultLessonModels from "./default-lesson-models"

interface Payload {
  cardStackId: string
  sectionId: string
  attachmentId: string
  newTitle: string
  originalAttachments: CardAttachment[]
}

export const LESSON_CARD_RENAME_ATTACHMENT = {
  name: "LESSON_CARD_RENAME_ATTACHMENT",

  patches(payload: Payload) {
    let newAttachments = cloneDeep(payload.originalAttachments)
    let attachment = find(newAttachments, (a) => a.id == payload.attachmentId)
    if (attachment === undefined) throw Error("Attachment not found")
    attachment.title = payload.newTitle

    let patch = Patch.create("card-stack", payload.cardStackId, "attributes.cards", {
      id: payload.sectionId,
    })

    patch.set("embedded", "attributes.attachments", newAttachments)

    return [patch.patch]
  },

  undoPatches(payload: Payload) {
    let patch = Patch.create("card-stack", payload.cardStackId, "attributes.cards", {
      id: payload.sectionId,
    })

    patch.set("embedded", "attributes.attachments", payload.originalAttachments)

    return [patch.patch]
  },

  narrative(payload: Payload, findFn: FindFn, userId): Narrative {
    let models = defaultLessonModels(payload, findFn, userId)
    let card = find(get(models, "cardStack.attributes.cards"), {
      id: payload.sectionId,
    })
    let cardTitle = get(card, "attributes.title")
    let attachmentTitle = payload.newTitle
    let context = defaultLessonContext(payload, models)
    let newContext = assign({}, context, {
      cardTitle: cardTitle,
      attachmentTitle,
    })
    let newEvent: LessonEditAttachment = {
      ...createBlankEvent(),
      root_id: models.planbook?.id ?? null,
      root_type: "planbook",
      date_plan_scheduled_for: models.dateStringForCardStack,
      primary_object_id: payload.cardStackId,
      primary_object_id_and_ancestor_ids: models.cardStackIdAndAncestorIds,
      primary_verb: "edit",
      primary_possessive: getPossessiveForPlanbook(findFn, userId, models.planbook?.id),
      primary_object_type: "lesson",
      primary_object_specifier: "attachments",

      adverbial_verb: "renaming",
      adverbial_object_type: "attachment",
      adverbial_object_specifier: null,
      adverbial_object_id: payload.attachmentId,
      adverbial_possessive: null,
      adverbial_object_id_and_ancestor_ids: models.cardStackIdAndAncestorIds,
    }

    return {
      title: "Renamed a File",
      titleForAnalytics: "Attachment - Renamed",
      description: `Renamed an attachment in "${cardTitle}" to ${payload.newTitle}`,
      context: newContext,
      activity: defaultLessonActivity(payload, models, "renamed"),
      newAnalyticsEvents: [newEvent],
    }
  },
}

export default LESSON_CARD_RENAME_ATTACHMENT
